"use client";

/**
 * VoiceDeviceProvider — the browser phone, on either carrier.
 *
 * TwilioDeviceProvider serves both: the voice token says which carrier the backend picked for
 * this agent (the one behind their numbers), and a Telnyx token gets a TelnyxRTC-backed device
 * with the Twilio SDK's surface (telnyx-device-adapter.ts). Every dialer runs the same code.
 * This wrapper stays for its import sites and the provider-neutral exports below.
 */

import React, { useContext } from "react";
import { TwilioDeviceProvider, TwilioDeviceContext, useTwilioDeviceContext } from "./TwilioDeviceContext";

// Dialer components import the call type and helpers from here rather than from an SDK.
export type { VoiceCall } from "./voice-call-helpers";
export { getCallSid, getCallParam, onCallDisconnect, hangupCall } from "./voice-call-helpers";

interface VoiceDeviceProviderProps {
  children: React.ReactNode;
  userId?: string;
}

export const VoiceDeviceProvider: React.FC<VoiceDeviceProviderProps> = ({ children, userId }) => (
  <TwilioDeviceProvider userId={userId}>{children}</TwilioDeviceProvider>
);

type VoiceDeviceContextValue = ReturnType<typeof useTwilioDeviceContext>;

export const useVoiceDeviceContext = (): VoiceDeviceContextValue => {
  const value = useContext(TwilioDeviceContext);
  if (!value) throw new Error("useVoiceDeviceContext must be used within VoiceDeviceProvider");
  return value;
};
